import type { Note } from './note';
import type { User, UserProfile } from './user';

type SessionParticipant = Omit<User, 'profile'> & {
  profile?: Pick<UserProfile, 'seatType'> | null;
};

export enum SessionType {
  Upcoming,
  Past,
}

export enum SessionListType {
  Next = 'Next',
  Past = 'Past',
  Upcoming = 'Upcoming',
}

export enum SessionStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DONE = 'done',
  RESCHEDULED = 'rescheduled',
}

export enum SessionFeedbackType {
  MIXED = 'mixed',
  NEGATIVE = 'negative',
  POSITIVE = 'positive',
}

export enum SessionWarningAction {
  CANCEL = 'CANCEL',
  RESCHEDULE = 'RESCHEDULE',
}

export type BookingSource = 'cal' | 'moments';

// Derived session classification served by SessionBlueprint#kind. Lets FE
// consumers branch on a single field instead of enumerating slug strings.
// Optional for backwards compatibility with cached payloads from before the
// backend started emitting it.
export type SessionKind = 'biweekly' | 'chemistry' | 'single';

export interface SessionFeedbackParams {
  background: string;
  border: string;
  chemistryCallSubtitle?: string;
  img: string;
  name: string;
  statsGraphColor: string;
  subtitle: string;
  title: string;
  type: SessionFeedbackType;
}

export type SessionCancelledBy = 'member' | 'coach' | 'system';

export interface Session {
  // MEN-4160: coach Schedule list-view metadata (see SessionBlueprint).
  attendanceVerification?: string;
  attended?: boolean;
  bookId: string;
  bookingSource?: BookingSource;
  cancelledBy?: SessionCancelledBy | null;
  cancellationRequestedBy?: SessionCancelledBy | null;
  coach: User;
  coachCancelAvailable?: boolean;
  coachId: string;
  coachRescheduleAvailable?: boolean;
  createdAt: string;
  endedAt: string;
  // MEN-4665: the Google Calendar event id this Session is synced to.
  // Matches the id returned by GET /calendar/external_events; the coach
  // schedule dedups the external event feed against this so a native
  // Mento appointment does not double-render alongside its own Google
  // projection. Undefined until SyncGoogleCalendarAndSessionsJob runs.
  eventId?: string | null;
  feedback?: SessionFeedback;
  id: string;
  isChemistryCall: boolean;
  kind?: SessionKind;
  location?: string;
  momentsBookingId?: string | null;
  notes?: Note[];
  recurringId: string;
  slug: string;
  sourceAppointmentCancelAllowed?: boolean;
  sourceAppointmentCancelInitiatorRequired?: boolean;
  sourceAppointmentCancelNearTermConfirmationRequired?: boolean;
  sourceAppointmentCancelOpsNoteRequired?: boolean;
  sourceAppointmentCancelReasonRequired?: boolean;
  sourceAppointmentCancelScopes?: Array<'appointment' | 'this_and_following'>;
  sourceAppointmentId?: string | null;
  // Which identity scheme `coachId` / `userId` / `coach.id` / `user.id` use on a
  // native appointment row. `appointmentActionSession` emits Moments person ids,
  // which are never comparable to a Rails `User.id`; `adminAppointmentActionSession`
  // emits Rails user ids, which are. Consumers must not infer this from the row
  // shape, because both adapters set `id === sourceAppointmentId`.
  sourceAppointmentIdentityScheme?: 'moments-person' | 'rails-user';
  sourceAppointmentRescheduleAllowed?: boolean;
  sourceAppointmentRescheduleScopes?: Array<
    'appointment' | 'this_and_following'
  >;
  sourceAppointmentSeriesId?: string | null;
  sourceAppointmentSeriesMutationVersion?: number | null;
  sourceAppointmentVersion?: number | null;
  startedAt: string;
  status: string;
  title: string;
  updatedAt?: Date;
  user: SessionParticipant;
  userId: string;
}

export const isMomentsSession = (
  session: Partial<Pick<Session, 'bookId' | 'bookingSource'>>,
) =>
  session.bookingSource === 'moments' ||
  session.bookId?.startsWith('moments:') === true;

export interface SessionFeedback {
  category: SessionFeedbackType;
  content: string;
  createdAt: Date;
  id: string;
  sessionId: string;
  updatedAt: Date;
  userId: string;
}

export interface CoachCadence {
  coachId: string;
  intervalWeeks: number;
  frequency: string;
  weekday: number | null;
  timeOfDay: string | null;
  timezone: string;
  contractEnd: {
    date: string | null;
    displayMode: 'contract_end' | 'ongoing';
  } | null;
  hasUpcoming: boolean;
}

export interface SessionDashboard {
  activeBiweekly?: Record<string, { bookingSource: string; seriesId: string }>;
  // Rails: the member holds a non-chemistry, non-cancelled session with a
  // current coach. Absent means unknown, never "not selected".
  coachSelected?: boolean;
  noSessions: string[];
  noUpcoming: string[];
  upcoming: Record<string, Session>;
  cadence: Record<string, CoachCadence>;
}

export interface CoachAttendanceReportRow {
  coachAttendanceSecondsTotal: number;
  lastSessionAt: string;
  memberAttendanceSecondsTotal: number;
  memberEmail: string;
  memberId: string;
  memberName: string;
  mismatchSessions: number;
  noDataSessions: number;
  totalSessions: number;
  unverifiedSessions: number;
  verifiedRate: number;
  verifiedSessions: number;
}

export interface CoachAttendanceReportResponse {
  current: number;
  hasNextPage: boolean;
  items: CoachAttendanceReportRow[];
  next: number;
  pages: number;
  previous: number;
  total: number;
}

export interface SessionUnavailabilityBlocker {
  blockingMemberEmail?: string | null;
  blockingMemberName?: string | null;
  bookingId: number | null;
  bookingUid: string | null;
  endsAt: string;
  eventTypeSlug?: string | null;
  hiddenInMemberContext: boolean;
  source: 'cal_booking' | 'external_calendar';
  sourceCalendarId?: string | null;
  startsAt: string;
}

export interface SessionUnavailabilityDiagnostics {
  blocked: boolean;
  blockers: SessionUnavailabilityBlocker[];
  coachEmail: string;
  complete: boolean;
  memberEmail?: string | null;
  slotEndsAt: string;
  slotStartsAt: string;
}

export interface SessionRescheduleOption {
  endsAt?: string;
  startsAt: string;
}

export interface SessionRescheduleOptionsResponse {
  options: SessionRescheduleOption[];
  requiresWithin48HoursConfirmation: boolean;
}

export interface SessionCount {
  done: number;
  last?: string;
  next?: string;
  pending: number;
}
